import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useMySites } from '../api/hooks'
import { useNetwork, type ActivityNode } from '../api/network'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { PageHeader } from '../components/PageHeader'
import { ActivityDetail } from './network/ActivityDetail'
import { ActivityEditorDialog } from './network/ActivityEditorDialog'
import { NetworkDiagram } from './network/NetworkDiagram'

/**
 * The site programme as a network.
 *
 * <p>Every box is joined to the four things that stop work: the drawing that has not
 * arrived, the material that is short, the trade that did not turn up and the BOQ line
 * that is behind. A red badge on a box means the site cannot proceed with it today, which
 * is a different and more useful statement than "this activity is late".</p>
 */
export function NetworkPage() {
  const sites = useMySites()
  const [siteId, setSiteId] = useState('')
  const chosenSite = siteId || sites.data?.[0]?.id || ''

  const network = useNetwork(chosenSite || undefined)
  const [selected, setSelected] = useState<ActivityNode | null>(null)
  const [editing, setEditing] = useState<ActivityNode | null>(null)
  const [creating, setCreating] = useState(false)

  // The selection is a snapshot; re-read it from the live network so the panel updates
  // when a linked drawing arrives or a quantity is recorded.
  const current = selected
    ? (network.data?.activities.find((a) => a.id === selected.id) ?? null)
    : null
  const summary = network.data?.summary

  return (
    <>
      <PageHeader
        title="Programme"
        subtitle="What has to happen before what, and what is stopping it."
        actions={
          chosenSite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              New activity
            </Button>
          )
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Site"
          value={chosenSite}
          onChange={(e) => {
            setSiteId(e.target.value)
            setSelected(null)
          }}
          sx={{ minWidth: 280 }}
        >
          {(sites.data ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.code} - {s.name}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {(sites.data?.length ?? 0) === 0 && !sites.isLoading && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">
            You have no sites assigned, so there is no programme to show.
          </Typography>
        </Paper>
      )}

      {network.isLoading && <LoadingBlock label="Calculating the network..." />}
      {network.error && <ErrorBlock error={network.error} />}

      {network.data?.warnings.map((warning) => (
        <Alert severity="error" key={warning} sx={{ mb: 2 }}>
          <AlertTitle>The network has a loop</AlertTitle>
          {warning}
        </Alert>
      ))}

      {summary && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
            mb: 2,
          }}
        >
          <Metric label="Activities" value={String(summary.activities)}
                  detail={`${summary.completed} done · ${summary.inProgress} running`} />
          <Metric label="Programme" value={`${summary.durationDays}d`}
                  detail={`${formatDate(summary.start)} → ${formatDate(summary.finish)}`} />
          <Metric label="On critical path" value={String(summary.criticalCount)}
                  detail="No float — a day lost here is a day lost overall" tone="critical" />
          <Metric label="Blocked today" value={String(summary.blockedCount)}
                  detail="Waiting on drawings, material or labour"
                  tone={summary.blockedCount > 0 ? 'critical' : undefined} />
          <Metric
            label="Progress"
            value={summary.percentComplete != null ? `${summary.percentComplete}%` : '—'}
            detail="Weighted from recorded BOQ quantities"
          />
        </Box>
      )}

      {network.data && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 380px' } }}>
          <Paper variant="outlined">
            <Box
              sx={{
                px: 2,
                pt: 2,
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 260 }}>
                Columns are dependency order, not calendar position. Click a box for detail.
              </Typography>
              <Legend colour="#b3261e" label="Critical path" />
              <Legend colour="#0d4a8a" label="Has float" />
              <Legend colour="#0b5d3b" label="Complete" />
            </Box>
            <NetworkDiagram
              activities={network.data.activities}
              dependencies={network.data.dependencies}
              selectedId={current?.id}
              onSelect={setSelected}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pb: 2 }}>
              Dates are calendar days from the site start date. Working calendars and holidays
              are not modelled yet, so a duration of five days spans a Sunday.
            </Typography>
          </Paper>

          <ActivityDetail
            activity={current}
            dependencies={network.data.dependencies}
            activities={network.data.activities}
            onEdit={() => current && setEditing(current)}
          />
        </Box>
      )}

      {(creating || editing) && chosenSite && (
        <ActivityEditorDialog
          siteId={chosenSite}
          activity={editing ?? undefined}
          activities={network.data?.activities ?? []}
          dependencies={network.data?.dependencies ?? []}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
        />
      )}
    </>
  )
}

function Metric({
  label,
  value,
  detail,
  tone,
}: {
  label: string
  value: string
  detail: string
  tone?: 'critical'
}) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: tone === 'critical' ? '#b3261e' : undefined }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      </CardContent>
    </Card>
  )
}

function Legend({ colour, label }: { colour: string; label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 14, height: 4, borderRadius: 2, bgcolor: colour }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

export function ProgressBar({ percent, behind }: { percent?: number; behind?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(percent ?? 0, 100)}
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          '& .MuiLinearProgress-bar': {
            backgroundColor: behind ? '#b3261e' : (percent ?? 0) >= 100 ? '#0b5d3b' : '#0d4a8a',
          },
        }}
      />
      <Typography variant="caption" sx={{ minWidth: 34, textAlign: 'right' }}>
        {percent != null ? `${percent}%` : '—'}
      </Typography>
    </Box>
  )
}

export function StatusChipSmall({ status }: { status: string }) {
  const tone: Record<string, { bg: string; fg: string }> = {
    NOT_STARTED: { bg: '#e6ebef', fg: '#4a5a68' },
    IN_PROGRESS: { bg: '#dceaf8', fg: '#0d4a8a' },
    COMPLETED: { bg: '#dcf2e6', fg: '#0b5d3b' },
    DELAYED: { bg: '#fbe0de', fg: '#8c1d18' },
    ON_HOLD: { bg: '#fdeecf', fg: '#9a4a00' },
  }
  const style = tone[status] ?? tone.NOT_STARTED
  return (
    <Chip
      size="small"
      label={status.replace('_', ' ').toLowerCase()}
      sx={{ bgcolor: style.bg, color: style.fg, fontWeight: 700, textTransform: 'capitalize' }}
    />
  )
}

function formatDate(value?: string): string {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' })
}
